import { useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Card,
} from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/LogoNavbar.png';
import { useRecordPageView } from '../hooks/useRecordPageView';
import { tracking } from '../util/tracking';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const location = useLocation();

  useRecordPageView();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    tracking.log('UI', 'login_submit', { page: 'login' });
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) setError(error.message);
    else setError('');
  };

  const showSuccessMessage =
    new URLSearchParams(location.search).get('success') === 'true';

  const translateError = (message: string) => {
    if (message.includes('Invalid login credentials')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (message.includes('User not found')) {
      return 'No account found with this email. Please sign up first.';
    }
    if (message.includes('Email not confirmed')) {
      return 'Your email address is not confirmed. Please check your inbox for a confirmation email.';
    }
    return message; // Default to original message for other errors
  };

  return (
    <div className="bg-light min-vh-100 d-flex align-items-center">
      <Container className="py-5">
        <Card className="shadow-lg rounded-3 overflow-hidden">
          <Row className="g-0">
            {/* Left column */}
            <Col
              md={6}
              className="bg-primary text-white p-4 d-flex flex-column justify-content-center align-items-center text-center"
            >
              <div>
                <img
                  src={logo}
                  alt="SupplyPassport Logo"
                  height="80"
                  className="mb-4"
                />
                <h2 className="fw-bold">Welcome to SupplyPassport</h2>
                <p className="mt-3 mb-0">
                  Securely manage your supply chain risk and compliance in one
                  place.
                </p>
              </div>
            </Col>

            {/* Right column */}
            <Col md={6} className="bg-white p-4">
              <div className="mb-4">
                <h2 className="h4 fw-bold mb-1">Sign in</h2>
                {/* <p className="text-muted mb-0">Access your dashboard</p> */}
              </div>

              {showSuccessMessage && (
                <Alert variant="success">
                  Your subscription has been confirmed. Please confirm your
                  email address by clicking the link in the confirmation email
                  we just sent you, then you can log in.
                </Alert>
              )}
              {error && <Alert variant="danger">{translateError(error)}</Alert>}

              <Form onSubmit={handleLogin} className="mb-3">
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label className="small">Email address</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-envelope" />
                    </span>
                    <Form.Control
                      type="email"
                      placeholder="you@company.com"
                      className="border-start-0"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formPassword">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="small mb-0">Password</Form.Label>
                    <Link to="/forgot-password" className="small">
                      Forgot?
                    </Link>
                  </div>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-lock" />
                    </span>
                    <Form.Control
                      type="password"
                      placeholder="••••••••"
                      className="border-start-0"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </Form.Group>

                <Button type="submit" className="w-100 btn-primary my-4">
                  Sign in
                </Button>
              </Form>

              <p className="text-center small mt-4 mb-0">
                Don’t have an account?{' '}
                <Link to="/register" className="fw-semibold">
                  Register here
                </Link>
              </p>
            </Col>
          </Row>
        </Card>

        <div className="text-center mt-3 small text-muted">
          By continuing, you agree to our{' '}
          <Link to="/terms-or-service" className="text-decoration-none">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy-policy" className="text-decoration-none">
            Privacy Policy
          </Link>
          .
        </div>
      </Container>
    </div>
  );
}
